import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import "./login.css";
import Swal from "sweetalert2";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { getData } from "country-list";
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaUserShield,
  FaBuilding,
  FaUsers,
  FaPhone,
  FaGlobe,
} from "react-icons/fa";
import { organizationAPI, branchAPI } from "../../services/api";
import { getCountryCurrency } from "../../utils/formatters";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Confirmation handed over by the change-password screen.
  useEffect(() => {
    const message = location.state?.message;
    if (message) {
      Swal.fire({ icon: "success", title: message, timer: 3500, showConfirmButton: false });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);
  const { login, register } = useAuth();
  const [isRightPanelActive, setIsRightPanelActive] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({
    fullName: "",
    email: "",
    username: "",
    password: "",
    organizationName: "",
    employeeSize: "",
    phone: "",
    country: "",
    countryCode: "",
    currency: "USD", // Default to USD
    currencySymbol: "$", // Default USD symbol
  });
  const [canRegister, setCanRegister] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [mathSymbols, setMathSymbols] = useState([]);
  const [overlaySymbols, setOverlaySymbols] = useState([]);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const animationRef = useRef(null);
  const overlayAnimationRef = useRef(null);
  const containerRef = useRef(null);

  // Get countries data from package
  const countryData = getData();

  // Employee size options
  const employeeSizeOptions = [
    { value: "1-10", label: "1-10 employees" },
    { value: "11-50", label: "11-50 employees" },
    { value: "51-200", label: "51-200 employees" },
    { value: "201-500", label: "201-500 employees" },
    { value: "501-1000", label: "501-1000 employees" },
    { value: "1000+", label: "1000+ employees" },
  ];

  useEffect(() => {
    setCanRegister(true);
    setIsLoading(false);
    initializePhysicsSymbols();
    initializeOverlaySymbols();
    autoDetectLocation();

    // Handle window resize to adjust particle count
    const handleResize = () => {
      // Cancel current animations
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (overlayAnimationRef.current) {
        cancelAnimationFrame(overlayAnimationRef.current);
      }

      // Reinitialize with new particle counts
      setTimeout(() => {
        initializePhysicsSymbols();
        initializeOverlaySymbols();
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (overlayAnimationRef.current) {
        cancelAnimationFrame(overlayAnimationRef.current);
      }
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Auto-detect user location and set country. Wrapped so a single
  // failed lookup (ipapi.co rate-limits its free tier with a 403 that
  // trips CORS) doesn't keep retrying on every mount — we cache the
  // result (success or failure) in sessionStorage for the tab session.
  const autoDetectLocation = async () => {
    const cachedFail = sessionStorage.getItem("geoLookupFailed");
    if (cachedFail === "1") return;
    const cachedOk = sessionStorage.getItem("geoLookupCountry");
    if (cachedOk) {
      try {
        const parsed = JSON.parse(cachedOk);
        setSignupData((prev) => ({ ...prev, ...parsed }));
      } catch (_) {}
      return;
    }

    // Do not call a third-party geo service from the browser. Its free
    // endpoint is rate-limited and may omit CORS headers; the signup form
    // remains usable with the stable default below.
    const patch = {
      country: "United States",
      countryCode: "US",
      currency: "USD",
      currencySymbol: "$",
    };
    try { sessionStorage.setItem("geoLookupCountry", JSON.stringify(patch)); } catch (_) {}
    setSignupData((prev) => ({ ...prev, ...patch }));
    setIsDetectingLocation(false);
  };

  // Get particle count based on screen size
  const getParticleCount = () => {
    const width = window.innerWidth;
    if (width >= 1200) return 300; // Desktop - more particles
    if (width >= 992) return 200; // Large tablet
    if (width >= 768) return 120; // Tablet
    if (width >= 480) return 60; // Mobile
    return 30; // Small mobile - minimal particles
  };

  const getOverlayParticleCount = () => {
    const width = window.innerWidth;
    if (width >= 1200) return 80; // Desktop
    if (width >= 992) return 60; // Large tablet
    if (width >= 768) return 40; // Tablet
    if (width >= 480) return 20; // Mobile
    return 10; // Small mobile
  };

  const initializePhysicsSymbols = () => {
    const symbols = [];
    const symbolChars = ["+", "-", "×", "÷", "=", "%", "∑", "∞", "π", "√"];
    const colors = [
      "#00d389",
      "#00b978",
      "#018d61",
      "#017e58",
      "#00a36c",
      "#00c380",
      "#00af70",
      "#019668",
      "#018a60",
      "#00b374",
    ];

    const particleCount = getParticleCount();
    for (let i = 0; i < particleCount; i++) {
      const size = Math.floor(Math.random() * 16) + 8;
      const char = symbolChars[Math.floor(Math.random() * symbolChars.length)];
      const color = colors[Math.floor(Math.random() * colors.length)];

      symbols.push({
        id: i,
        char: char,
        size: size,
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        vx: (Math.random() - 0.5) * 1.5,
        vy: (Math.random() - 0.5) * 1.5,
        mass: size,
        color: color,
        isColliding: false,
      });
    }

    setMathSymbols(symbols);
    animateSymbols(symbols);
  };

  const initializeOverlaySymbols = () => {
    const symbols = [];
    const symbolChars = ["+", "-", "×", "÷", "=", "%", "∑", "∞", "π", "√"];

    const overlayParticleCount = getOverlayParticleCount();
    for (let i = 0; i < overlayParticleCount; i++) {
      const size = Math.floor(Math.random() * 12) + 6;
      const char = symbolChars[Math.floor(Math.random() * symbolChars.length)];

      symbols.push({
        id: i,
        char: char,
        size: size,
        x: Math.random() * 100,
        y: Math.random() * 100,
        vx: (Math.random() - 0.5) * 0.6,
        vy: (Math.random() - 0.5) * 0.6,
        mass: size,
        isColliding: false,
      });
    }

    setOverlaySymbols(symbols);
    animateOverlaySymbols(symbols);
  };

  const animateSymbols = (symbols) => {
    const updatePhysics = () => {
      const updatedSymbols = [...symbols];

      for (let i = 0; i < updatedSymbols.length; i++) {
        const symbol = updatedSymbols[i];

        symbol.x += symbol.vx;
        symbol.y += symbol.vy;

        if (
          symbol.x < symbol.size ||
          symbol.x > window.innerWidth - symbol.size
        ) {
          symbol.vx = -symbol.vx;
          symbol.x =
            symbol.x < symbol.size
              ? symbol.size
              : window.innerWidth - symbol.size;
        }

        if (
          symbol.y < symbol.size ||
          symbol.y > window.innerHeight - symbol.size
        ) {
          symbol.vy = -symbol.vy;
          symbol.y =
            symbol.y < symbol.size
              ? symbol.size
              : window.innerHeight - symbol.size;
        }

        symbol.isColliding = false;
      }

      for (let i = 0; i < updatedSymbols.length; i++) {
        const s1 = updatedSymbols[i];

        if (
          s1.x > -50 &&
          s1.x < window.innerWidth + 50 &&
          s1.y > -50 &&
          s1.y < window.innerHeight + 50
        ) {
          for (let j = i + 1; j < updatedSymbols.length; j++) {
            const s2 = updatedSymbols[j];

            const dx = s2.x - s1.x;
            const dy = s2.y - s1.y;
            const distanceSquared = dx * dx + dy * dy;
            const minDistance = s1.size + s2.size;

            if (distanceSquared < minDistance * minDistance) {
              const distance = Math.sqrt(distanceSquared);

              if (distance < minDistance) {
                s1.isColliding = true;
                s2.isColliding = true;

                const angle = Math.atan2(dy, dx);
                const sin = Math.sin(angle);
                const cos = Math.cos(angle);

                const vx1 = s1.vx * cos + s1.vy * sin;
                const vy1 = s1.vy * cos - s1.vx * sin;
                const vx2 = s2.vx * cos + s2.vy * sin;
                const vy2 = s2.vy * cos - s2.vx * sin;

                const v1f =
                  ((s1.mass - s2.mass) * vx1 + 2 * s2.mass * vx2) /
                  (s1.mass + s2.mass);
                const v2f =
                  ((s2.mass - s1.mass) * vx2 + 2 * s1.mass * vx1) /
                  (s1.mass + s2.mass);

                s1.vx = v1f * cos - vy1 * sin;
                s1.vy = vy1 * cos + v1f * sin;
                s2.vx = v2f * cos - vy2 * sin;
                s2.vy = vy2 * cos + v2f * sin;

                const overlap = (minDistance - distance) / 2;
                const moveX = (overlap * dx) / distance;
                const moveY = (overlap * dy) / distance;

                s1.x -= moveX;
                s1.y -= moveY;
                s2.x += moveX;
                s2.y += moveY;
              }
            }
          }
        }
      }

      setMathSymbols(updatedSymbols);
      animationRef.current = requestAnimationFrame(updatePhysics);
    };

    animationRef.current = requestAnimationFrame(updatePhysics);
  };

  const animateOverlaySymbols = (symbols) => {
    const updatePhysics = () => {
      const updatedSymbols = [...symbols];

      for (let i = 0; i < updatedSymbols.length; i++) {
        const symbol = updatedSymbols[i];

        symbol.x += symbol.vx;
        symbol.y += symbol.vy;

        if (symbol.x < 0 || symbol.x > 100) {
          symbol.vx = -symbol.vx;
          symbol.x = symbol.x < 0 ? 0 : 100;
        }

        if (symbol.y < 0 || symbol.y > 100) {
          symbol.vy = -symbol.vy;
          symbol.y = symbol.y < 0 ? 0 : 100;
        }

        symbol.isColliding = false;
      }

      for (let i = 0; i < updatedSymbols.length; i++) {
        const s1 = updatedSymbols[i];

        for (let j = i + 1; j < updatedSymbols.length; j++) {
          const s2 = updatedSymbols[j];

          const dx = s2.x - s1.x;
          const dy = s2.y - s1.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const minDistance = 5;

          if (distance < minDistance) {
            s1.isColliding = true;
            s2.isColliding = true;

            const angle = Math.atan2(dy, dx);
            const targetX = s1.x + Math.cos(angle) * minDistance;
            const targetY = s1.y + Math.sin(angle) * minDistance;

            const ax = (targetX - s2.x) * 0.05;
            const ay = (targetY - s2.y) * 0.05;

            s1.vx -= ax;
            s1.vy -= ay;
            s2.vx += ax;
            s2.vy += ay;
          }
        }
      }

      setOverlaySymbols(updatedSymbols);
      overlayAnimationRef.current = requestAnimationFrame(updatePhysics);
    };

    overlayAnimationRef.current = requestAnimationFrame(updatePhysics);
  };

  const handleSignUpClick = () => {
    setIsRightPanelActive(true);
  };

  const handleSignInClick = () => {
    setIsRightPanelActive(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      if (!loginData.email || !loginData.password) {
        throw new Error("Please fill in all fields");
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(loginData.email)) {
        throw new Error("Please enter a valid email address");
      }

      Swal.fire({
        title: "Signing in...",
        text: "Please wait",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
        backdrop: `rgba(0,211,137,0.1)`,
      });

      const result = await login(loginData.email, loginData.password);

      Swal.close();

      // Check if login was successful
      if (result && result.token) {
        // Check user role and subscription plan for redirect
        const userRole = result.user?.role;
        const userPlan = result.user?.subscriptionPlan || "free";

        // Access is governed by per-org pageRoles now, not by plan gates,
        // so every authenticated user lands on the dashboard — unless an
        // admin issued a temporary password that must be replaced first.
        navigate(result.user?.mustChangePassword ? "/change-password" : "/dashboard");
      } else if (result && result.error) {
        // Handle API error response
        throw new Error(result.error);
      } else if (result && result.message) {
        // Handle API message response
        throw new Error(result.message);
      } else {
        // Handle generic failure
        throw new Error("Login failed - please check your credentials and try again");
      }
    } catch (error) {
      console.error("Login error:", error);
      Swal.close();
      
      // Enhanced error handling with better messages
      let errorTitle = "Login Failed";
      let errorMessage = "An error occurred during login. Please try again.";
      
      // Safe error message extraction
      if (error && typeof error === 'object') {
        // Check for backend specific error messages first
        if (error.error && typeof error.error === 'string') {
          const errorType = error.error.toLowerCase();
          
          if (errorType.includes("invalid password") || errorType.includes("incorrect password")) {
            errorTitle = "Incorrect Password";
            errorMessage = "The password you entered is incorrect. Please try again.";
          } else if (errorType.includes("user not found") || errorType.includes("no user")) {
            errorTitle = "User Not Found";
            errorMessage = "No account found with this email address. Please check your email or create a new account.";
          } else if (errorType.includes("invalid email") || errorType.includes("email format")) {
            errorTitle = "Invalid Email Format";
            errorMessage = "Please enter a valid email address format.";
          } else if (errorType.includes("invalid credentials")) {
            errorTitle = "Invalid Credentials";
            errorMessage = "The email or password you entered is incorrect. Please check your credentials and try again.";
          } else {
            // Use the specific error message from backend
            errorTitle = "Login Failed";
            errorMessage = error.message || error.error;
          }
        } else if (error.message) {
          // Handle general error messages
          const errorMsg = error.message.toLowerCase();
          
          if (errorMsg.includes("invalid") || 
              errorMsg.includes("incorrect") ||
              errorMsg.includes("wrong") ||
              errorMsg.includes("credentials") ||
              errorMsg.includes("failed")) {
            errorTitle = "Invalid Credentials";
            errorMessage = "The email or password you entered is incorrect. Please check your credentials and try again.";
          } else if (errorMsg.includes("not found") ||
                     errorMsg.includes("user not found") ||
                     errorMsg.includes("no user")) {
            errorTitle = "User Not Found";
            errorMessage = "No account found with this email address. Please check your email or create a new account.";
          } else if (errorMsg.includes("password")) {
            errorTitle = "Invalid Password";
            errorMessage = "The password you entered is incorrect. Please try again.";
          } else if (errorMsg.includes("email")) {
            errorTitle = "Invalid Email";
            errorMessage = "Please enter a valid email address.";
          } else if (errorMsg.includes("network") ||
                     errorMsg.includes("connection") ||
                     errorMsg.includes("timeout")) {
            errorTitle = "Connection Error";
            errorMessage = "Unable to connect to the server. Please check your internet connection and try again.";
          } else if (errorMsg.includes("server") ||
                     errorMsg.includes("internal")) {
            errorTitle = "Server Error";
            errorMessage = "Something went wrong on our end. Please try again in a few moments.";
          } else {
            errorMessage = error.message;
          }
        } else if (error.status) {
          // Handle HTTP status errors
          if (error.status === 401) {
            errorTitle = "Authentication Failed";
            errorMessage = "Please check your credentials and try again.";
          } else if (error.status === 404) {
            errorTitle = "User Not Found";
            errorMessage = "No account found with this email address.";
          } else if (error.status >= 500) {
            errorTitle = "Server Error";
            errorMessage = "Something went wrong on our end. Please try again in a few moments.";
          } else {
            errorTitle = "Request Failed";
            errorMessage = `Request failed with status: ${error.status}`;
          }
        } else {
          errorMessage = "An unexpected error occurred. Please try again.";
        }
      } else if (error && typeof error === 'string') {
        // Handle string errors
        errorMessage = error;
      } else {
        // Handle undefined or null errors
        errorMessage = "An unexpected error occurred. Please try again.";
      }

      Swal.fire({
        icon: "error",
        title: errorTitle,
        text: errorMessage,
        confirmButtonColor: "#00d389",
        confirmButtonText: "Try Again",
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp'
        },
        customClass: {
          confirmButton: 'swal2-confirm-custom',
          popup: 'swal2-popup-custom'
        }
      });
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    try {
      // Enhanced validation
      if (
        !signupData.fullName ||
        !signupData.email ||
        !signupData.username ||
        !signupData.password ||
        !signupData.organizationName ||
        !signupData.employeeSize ||
        !signupData.country
      ) {
        throw new Error(
          "Please fill in all required fields, including country"
        );
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(signupData.email)) {
        throw new Error("Please enter a valid email address");
      }

      if (signupData.password.length < 6) {
        throw new Error("Password must be at least 6 characters long");
      }

      if (signupData.phone && signupData.phone.length < 10) {
        throw new Error("Please enter a valid phone number");
      }

      Swal.fire({
        title: "Creating account...",
        text: "Please wait",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
        backdrop: `rgba(0,211,137,0.1)`,
      });

      try {
        const result = await register({
          email: signupData.email,
          password: signupData.password,
          fullName: signupData.fullName,
          username: signupData.username,
          organizationName: signupData.organizationName,
          employeeSize: signupData.employeeSize,
          phone: signupData.phone,
          country: signupData.country,
          currency: signupData.currency,
          currencySymbol: signupData.currencySymbol,
        });

        Swal.close();

        if (result && result.success) {
          // Save currency settings to localStorage after successful registration
          if (signupData.country && signupData.currency && signupData.currencySymbol) {
            localStorage.setItem("country", signupData.country);
            localStorage.setItem("countryCode", signupData.countryCode || "");
            localStorage.setItem("currency", signupData.currency);
            localStorage.setItem("currencySymbol", signupData.currencySymbol);
            
            console.log(`Currency settings saved after registration: ${signupData.currency} (${signupData.currencySymbol}) for ${signupData.country}`);
          }

          Swal.fire({
            icon: "success",
            title: "Registration Successful",
            text: result.message || "Your account has been created successfully! Please login to continue.",
            confirmButtonColor: "#00d389",
          });

          // Clear any stored auth data to force login
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          
          // Reset form data
          setSignupData({
            fullName: "",
            email: "",
            username: "",
            password: "",
            organizationName: "",
            employeeSize: "",
            phone: "",
            country: "",
            countryCode: "",
            currency: "USD",
            currencySymbol: "$",
          });

          // Switch to login panel
          setIsRightPanelActive(false);

          // Pre-fill email in login form if available
          if (result.userEmail) {
            setLoginData(prev => ({
              ...prev,
              email: result.userEmail
            }));
          }

          setTimeout(() => {
            // Show login message
            Swal.fire({
              icon: "info",
              title: "Please Login",
              text: "Your account has been created. Please login with your credentials to access the dashboard.",
              confirmButtonColor: "#00d389",
            });
          }, 1000);
        } else {
          throw new Error("Registration failed - no success response received");
        }
      } catch (apiError) {
        console.error("Signup API error:", apiError);
        Swal.close();

        let errorMessage = "Registration failed. Please try again.";

        if (apiError && typeof apiError === "object") {
          if (apiError.error) {
            errorMessage = apiError.error;
          } else if (apiError.message) {
            errorMessage = apiError.message;
          } else if (apiError.status) {
            errorMessage = `Request failed with status: ${apiError.status}`;
          } else {
            errorMessage = "An unexpected error occurred during registration.";
          }
        } else if (typeof apiError === "string") {
          errorMessage = apiError;
        } else if (apiError) {
          errorMessage = String(apiError);
        }

        Swal.fire({
          icon: "error",
          title: "Registration Failed",
          text: errorMessage,
          confirmButtonColor: "#00d389",
          confirmButtonText: "Try Again",
          showClass: {
            popup: 'animate__animated animate__fadeInDown'
          },
          hideClass: {
            popup: 'animate__animated animate__fadeOutUp'
          }
        });
      }
    } catch (validationError) {
      console.error("Signup validation error:", validationError);
      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: validationError.message,
        confirmButtonColor: "#00d389",
        confirmButtonText: "Fix Issues",
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp'
        }
      });
    }
  };

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSignupInputChange = (e) => {
    const { name, value } = e.target;
    setSignupData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCountryChange = (e) => {
    const selectedCountryName = e.target.value;
    const selectedCountry = countryData.find(
      (country) => country.name === selectedCountryName
    );

    // Auto-detect currency based on selected country
    const detectedCurrency = getCountryCurrency(
      selectedCountryName,
      selectedCountry?.code || ""
    );

    setSignupData((prev) => ({
      ...prev,
      country: selectedCountryName,
      countryCode: selectedCountry ? selectedCountry.code : "",
      currency: detectedCurrency.code,
      currencySymbol: detectedCurrency.symbol,
    }));

    // Update localStorage to trigger currency refresh
    if (selectedCountryName) {
      localStorage.setItem("country", selectedCountryName);
      localStorage.setItem("countryCode", selectedCountry?.code || "");
      localStorage.setItem("currency", detectedCurrency.code);
      localStorage.setItem("currencySymbol", detectedCurrency.symbol);
      
      // Force currency refresh to fix any mismatches
      setTimeout(() => {
        const { forceCurrencyRefresh } = require("../../utils/countryBasedCurrency");
        forceCurrencyRefresh();
      }, 100);
    }

    // Show currency detection notification
    if (selectedCountryName && detectedCurrency.code) {
      console.log(
        `Country selected: ${selectedCountryName}, Currency auto-detected: ${detectedCurrency.code} (${detectedCurrency.symbol})`
      );

      // Show a subtle notification
      setTimeout(() => {
        const notification = document.createElement("div");
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(45deg, #00d389, #00b978);
          color: white;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          z-index: 10000;
          animation: slideInRight 0.3s ease-out;
          box-shadow: 0 4px 12px rgba(0, 211, 137, 0.3);
        `;
        notification.innerHTML = `
          <div style="display: flex; align-items: center; gap: 8px;">
            <span>💰</span>
            <span><strong>Currency Auto-Detected:</strong> ${detectedCurrency.name} (${detectedCurrency.code})</span>
          </div>
        `;

        document.body.appendChild(notification);

        setTimeout(() => {
          notification.style.animation = "slideOutRight 0.3s ease-in forwards";
          setTimeout(() => {
            document.body.removeChild(notification);
          }, 300);
        }, 3000);
      }, 100);
    }
  };

  const handlePhoneChange = (phone, country) => {
    setSignupData((prev) => ({
      ...prev,
      phone: phone || "",
      countryCode: country?.countryCode?.toUpperCase() || prev.countryCode,
    }));

    // Update country name if country object is available
    if (country && country.name) {
      const selectedCountry = countryData.find(
        (c) => c.code === country.countryCode?.toUpperCase()
      );

      if (selectedCountry) {
        // Auto-detect currency based on phone number country
        const detectedCurrency = getCountryCurrency(
          selectedCountry.name,
          country.countryCode?.toUpperCase()
        );

        setSignupData((prev) => ({
          ...prev,
          country: selectedCountry.name,
          countryCode: country.countryCode?.toUpperCase(),
          currency: detectedCurrency.code,
          currencySymbol: detectedCurrency.symbol,
        }));

        console.log(
          `Phone country detected: ${selectedCountry.name}, Currency: ${detectedCurrency.code}`
        );
      }
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="login" ref={containerRef}>
      {/* Math symbols with collision physics */}
      <div className="math-symbols-container">
        {mathSymbols.map((symbol) => (
          <div
            key={symbol.id}
            className={`math-symbol ${symbol.isColliding ? "collision" : ""}`}
            style={{
              left: `${symbol.x}px`,
              top: `${symbol.y}px`,
              fontSize: `${symbol.size}px`,
              width: `${symbol.size * 1.5}px`,
              height: `${symbol.size * 1.5}px`,
              color: symbol.color,
              backgroundColor: `${symbol.color}10`,
              boxShadow: symbol.isColliding
                ? `0 0 10px ${symbol.color}`
                : "none",
              zIndex: symbol.isColliding ? 1 : 0,
            }}
          >
            {symbol.char}
          </div>
        ))}
      </div>

      <div
        className={`container-login ${
          isRightPanelActive ? "right-panel-active-login" : ""
        }`}
      >
        <div className="form-container-login sign-up-container-login">
          <form className="form-login" onSubmit={handleSignup}>
            <h1 className="h1-login" style={{ color: "#00d389" }}>
              Create Account
            </h1>
            <span className="span-login">Setup your organization profile</span>

            {/* Personal Information */}
            <div style={{ position: "relative", width: "100%" }}>
              <FaUser
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "22px",
                  color: "#aaa",
                }}
              />
              <input
                type="text"
                placeholder="Full Name *"
                name="fullName"
                className="input-login"
                style={{ paddingLeft: "40px" }}
                value={signupData.fullName}
                onChange={handleSignupInputChange}
                autoComplete="name"
                required
              />
            </div>

            <div style={{ position: "relative", width: "100%" }}>
              <FaUserShield
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "22px",
                  color: "#aaa",
                }}
              />
              <input
                type="text"
                placeholder="Username *"
                name="username"
                className="input-login"
                style={{ paddingLeft: "40px" }}
                value={signupData.username}
                onChange={handleSignupInputChange}
                autoComplete="username"
                required
              />
            </div>

            {/* Location & Contact */}
            <div style={{ position: "relative", width: "100%" }}>
              <FaGlobe
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "22px",
                  color: "#aaa",
                  zIndex: 1,
                }}
              />
              <select
                name="country"
                className="input-login"
                style={{ paddingLeft: "40px", appearance: "none" }}
                value={signupData.country}
                onChange={handleCountryChange}
                disabled={isDetectingLocation}
                autoComplete="country"
              >
                <option value="">
                  {isDetectingLocation
                    ? "Detecting location..."
                    : "Select Country"}
                </option>
                {countryData.map((country) => (
                  <option key={country.code} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ position: "relative", width: "100%" }}>
              <FaPhone
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "22px",
                  color: "#aaa",
                  zIndex: 2,
                }}
              />

              <PhoneInput
                className="input-login"
                type="text"
                name="phone"
                style={{ paddingLeft: "40px" }}
                country={signupData.countryCode?.toLowerCase() || ""}
                value={signupData.phone}
                onChange={handlePhoneChange}
                placeholder="Phone Number"
                containerClass="phone-input-container custom-phone-input"
                buttonClass="custom-phone-country-button"
                dropdownClass="custom-phone-dropdown"
                searchClass="custom-phone-search"
                enableSearch={true}
                disableSearchIcon={true}
                searchPlaceholder="Type to search..."
                countryCodeEditable={false}
                autoComplete="tel"
                searchNotFound="No country found"
                searchStyle={{
                  margin: "0",
                  width: "100%",
                  padding: "8px 12px",
                  border: "none",
                  borderBottom: "1px solid #e0e6ed",
                  fontSize: "14px",
                  outline: "none",
                  backgroundColor: "#f8fafc",
                }}
                buttonStyle={{
                  border: "none",
                  backgroundColor: "transparent",
                  borderRadius: "0",
                }}
                dropdownStyle={{
                  position: "absolute",
                  top: "100%",
                  left: "0",
                  right: "0",
                  backgroundColor: "white",
                  border: "1px solid #e0e6ed",
                  borderRadius: "8px",
                  boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                  maxHeight: "250px",
                  zIndex: 9999,
                  overflow: "hidden",
                }}
              >
                <input
                  className="input-login"
                  type="text"
                  name="phone"
                  style={{ paddingLeft: "40px" }}
                />
              </PhoneInput>
            </div>

            <div style={{ position: "relative", width: "100%" }}>
              <FaBuilding
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "22px",
                  color: "#aaa",
                }}
              />
              <input
                type="text"
                placeholder="Organization Name *"
                name="organizationName"
                className="input-login"
                style={{ paddingLeft: "40px" }}
                value={signupData.organizationName}
                onChange={handleSignupInputChange}
                autoComplete="organization"
                required
              />
            </div>

            <div style={{ position: "relative", width: "100%" }}>
              <FaUsers
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "22px",
                  color: "#aaa",
                }}
              />
              <select
                name="employeeSize"
                className="input-login"
                style={{ paddingLeft: "40px", appearance: "none" }}
                value={signupData.employeeSize}
                onChange={handleSignupInputChange}
                required
              >
                <option value="">Select Company Size *</option>
                {employeeSizeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ position: "relative", width: "100%" }}>
              <FaEnvelope
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "22px",
                  color: "#aaa",
                }}
              />
              <input
                type="email"
                placeholder="Email Address *"
                name="email"
                className="input-login"
                style={{ paddingLeft: "40px" }}
                value={signupData.email}
                onChange={handleSignupInputChange}
                autoComplete="email"
                required
              />
            </div>

            <div style={{ position: "relative", width: "100%" }}>
              <FaLock
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "22px",
                  color: "#aaa",
                }}
              />
              <input
                type="password"
                placeholder="Password (min 6 chars) *"
                name="password"
                className="input-login"
                style={{ paddingLeft: "40px" }}
                value={signupData.password}
                onChange={handleSignupInputChange}
                autoComplete="new-password"
                required
              />
            </div>

            <button type="submit" className="button-login">
              Create Organization
            </button>

            {/* Mobile-friendly signin link */}
            <div className="mobile-auth-switch">
              <p>
                Already have an account?{" "}
                <button
                  type="button"
                  className="auth-switch-link"
                  onClick={handleSignInClick}
                >
                  Sign In
                </button>
              </p>
            </div>

            <small
              style={{ color: "#666", marginTop: "10px", fontSize: "12px" }}
            >
              * Required fields
            </small>
          </form>
        </div>

        <div className="form-container-login sign-in-container-login">
          <form className="form-login" onSubmit={handleLogin}>
            <h1 className="h1-login" style={{ color: "#00d389" }}>
              Sign in
            </h1>
            <span className="span-login">
              Use your account to access the system
            </span>
            <div style={{ position: "relative", width: "100%" }}>
              <FaEnvelope
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "22px",
                  color: "#aaa",
                }}
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                className="input-login"
                style={{ paddingLeft: "40px" }}
                value={loginData.email}
                onChange={handleLoginInputChange}
                autoComplete="email"
              />
            </div>
            <div style={{ position: "relative", width: "100%" }}>
              <FaLock
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "22px",
                  color: "#aaa",
                }}
              />
              <input
                type="password"
                name="password"
                placeholder="Password"
                className="input-login"
                style={{ paddingLeft: "40px" }}
                value={loginData.password}
                onChange={handleLoginInputChange}
                autoComplete="current-password"
              />
            </div>
            <button type="submit" className="button-login">
              Sign In
            </button>

            {/* Mobile-friendly signup link */}
            <div className="mobile-auth-switch">
              <p>
                Don't have an account?{" "}
                <button
                  type="button"
                  className="auth-switch-link"
                  onClick={handleSignUpClick}
                >
                  Sign up here
                </button>
              </p>
            </div>
          </form>
        </div>

        <div className="overlay-container-login">
          <div className="overlay-login">
            {/* Add overlay math symbols */}
            <div className="overlay-math-symbols">
              {overlaySymbols.map((symbol) => (
                <div
                  key={symbol.id}
                  className={`overlay-math-symbol ${
                    symbol.isColliding ? "collision" : ""
                  }`}
                  style={{
                    left: `${symbol.x}%`,
                    top: `${symbol.y}%`,
                    fontSize: `${symbol.size}px`,
                    width: `${symbol.size * 1.5}px`,
                    height: `${symbol.size * 1.5}px`,
                    color: "#fff",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    boxShadow: symbol.isColliding
                      ? "0 0 10px rgba(255, 255, 255, 0.3)"
                      : "none",
                    zIndex: symbol.isColliding ? 1 : 0,
                  }}
                >
                  {symbol.char}
                </div>
              ))}
            </div>
            <div className="overlay-panel-login overlay-left-login">
              <h1 className="h1-login">Welcome Back!</h1>
              <p className="p-login">Please login with your personal info</p>
              <button
                className="button-login ghost-login"
                onClick={handleSignInClick}
              >
                Sign In
              </button>
            </div>
            <div className="overlay-panel-login overlay-right-login">
              <h1 className="h1-login">First Time?</h1>
              <p className="p-login">
                Create your organization account to get started
              </p>
              <button
                className="button-login ghost-login"
                onClick={handleSignUpClick}
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
